import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MessageTypeEnum } from '@app/ngrx-message/constants/messageTypeEnum';
import { Message } from '@app/ngrx-message/models/client/message';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import {
  AI_LIST_ACTIONS,
  selectAIListState,
  selectSelectedAiList,
} from '@usf/ngrx-list-ai';
import { combineLatest, filter, tap, withLatestFrom } from 'rxjs';
import { LoadingState, ProductStateService } from '@usf/ngrx-product';
import { MenuController } from '@ionic/angular';
import { AIListBuilderService } from '../../services/ai-list-builder.service';
import { alteranitiveIngredientsSelector } from '../../selectors/ai-list-view-model.selector';
import { AIList } from '@usf/list-ai-types';

@Injectable({
  providedIn: 'root',
})
export class ClientAiListEffects {
  loadAiListSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AI_LIST_ACTIONS.loadListAiSuccess),
        withLatestFrom(this.store.select(selectSelectedAiList)),
        filter(([action, selectedAiList]: [any, AIList]) =>
          action.aiLists.some(
            updatedList => updatedList.aiListId === selectedAiList?.aiListId,
          ),
        ),
        tap(([action, selectedAiList]: [any, AIList]) => {
          // Extract product numbers from the updated list
          const productNumbers = selectedAiList.groups
            .reduce((acc, group) => acc.concat(group?.groupItems ?? []), [])
            .filter(item => item?.match?.productNumber)
            .map(item => item?.match?.productNumber);

          // Load the products
          this.productStateService.loadProductsWithRetry(productNumbers);
        }),
      ),
    { dispatch: false },
  );

  create$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AI_LIST_ACTIONS.create),
        tap(action => this.loadingSpinnerService.createSpinnerModal()),
      ),
    { dispatch: false },
  );

  createSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AI_LIST_ACTIONS.createSuccess),
        tap(() => this.loadingSpinnerService.dismissSpinnerModal()),
        tap(action =>
          this.router.navigate(['lists', 'aiList', action.aiList.aiListId]),
        ),
      ),
    { dispatch: false },
  );

  createFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AI_LIST_ACTIONS.createFailed),
        tap(() => this.loadingSpinnerService.dismissSpinnerModal()),
        tap(action =>
          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.error,
            display: this.translateService.instant(
              'i18n.lists.aiListCreationFail',
            ),
            toast: true,
            sticky: false,
          } as Message),
        ),
      ),
    { dispatch: false },
  );

  searchSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AI_LIST_ACTIONS.searchSuccess),
        tap(action => {
          const productNumbers = action.response.products.map(
            p => p.productNumber,
          );
          this.productStateService.loadProductsWithRetry(productNumbers);
        }),
      ),
    { dispatch: false },
  );

  searchFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AI_LIST_ACTIONS.searchFail),
        tap(() => {
          // Close the menu using MenuController
          this.menuController.close('ingredient-menu');

          this.messageStoreService.upsertMessage({
            type: MessageTypeEnum.error,
            display: this.translateService.instant(
              'i18n.lists.aiListEditMenu.errorMessage',
            ),
            toast: true,
            sticky: false,
          } as Message);
        }),
      ),
    { dispatch: false },
  );

  // // Close ingredient menu when alternativeIngredients array is empty and loading state is 'loaded'
  // closeEmptyIngredientsMenu$ = createEffect(
  //   () =>
  //     combineLatest([
  //       this.store.select(selectAIListState),
  //       this.store.select(alteranitiveIngredientsSelector),
  //       this.aiListBuilderService.selectAlternativeIngredientsLoading(),
  //     ]).pipe(

  //       filter(
  //         ([listState, ingredients, loadingState]) =>
  //           !!listState && listState?.loaded && listState?.searchResults && ingredients.length === 0 && loadingState === LoadingState.loaded,
  //       ),
  //       tap(() => {
  //         // Close the ingredient menu
  //         this.menuController.close('ingredient-menu');

  //         // Show message about no alternatives found
  //         this.messageStoreService.upsertMessage({
  //           type: MessageTypeEnum.warning,
  //           display: this.translateService.instant(
  //             'i18n.lists.aiListEditMenu.errorMessage',
  //           ),
  //           toast: true,
  //           sticky: false,
  //         } as Message);
  //       }),
  //     ),
  //   { dispatch: false },
  // );

  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly loadingSpinnerService: LoadingSpinnerService,
    private readonly messageStoreService: MessageStoreService,
    private readonly translateService: TranslateService,
    private readonly productStateService: ProductStateService,
    private readonly menuController: MenuController,
    private readonly store: Store,
    private readonly aiListBuilderService: AIListBuilderService,
  ) {}
}
